/**
 * Which roles satisfy a route's `requiresRole`, and where a role belongs when it lands on the
 * wrong portal.
 *
 * An allow-list rather than a list of rejected pairs. The guard used to turn away only USER at
 * /admin and ADMIN at /, which left COMPANY_ADMIN — and any role string we did not anticipate —
 * walking straight into the admin portal, where the shell renders and every request 403s.
 *
 * This is a navigation concern, not a security boundary: the backend refuses these calls either
 * way. What it fixes is the user being shown a portal they cannot use.
 */

const ADMIN_PORTAL = "/admin";
const CUSTOMER_PORTAL = "/";

/** The roles each `requiresRole` value admits. Unknown requirements admit nobody. */
const ALLOWED_ROLES: Record<string, readonly string[]> = {
  ADMIN: ["ADMIN"],
  USER: ["USER", "COMPANY_ADMIN"],
};

export const isRoleAllowed = (requiredRole: unknown, role: string): boolean => {
  if (typeof requiredRole !== "string") {
    return true;
  }
  return (ALLOWED_ROLES[requiredRole] ?? []).includes(role);
};

/**
 * The portal a role belongs to, or null for a role we do not recognise — which can only mean
 * the session is unusable. Returning a portal there would bounce the user between two routes
 * that both reject them, so the caller sends them to login instead.
 */
export const portalHomeFor = (role: string): string | null => {
  if (role === "ADMIN") {
    return ADMIN_PORTAL;
  }
  if (role === "USER" || role === "COMPANY_ADMIN") {
    return CUSTOMER_PORTAL;
  }
  return null;
};

/**
 * The login page belonging to a route. The catch-all carries no role requirement — it serves
 * both portals — so the path is the fallback: a signed-out visitor on a bad /admin/... URL
 * should still land on the admin login rather than the customer one.
 */
export const loginPathFor = (requiredRole: unknown, path = ""): string =>
  requiredRole === "ADMIN" || path.startsWith(ADMIN_PORTAL)
    ? "/admin/login"
    : "/login";
